import { useNavigate } from "@tanstack/react-router";
import { useCustomerAppointments } from "@/api/hooks/customer.hooks";
import { formatAppointmentTime, formatDisplayDate } from "@/utils/date";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Calendar, Clock } from "lucide-react";
import { VisitType } from "@/api/enums";

export default function PatientAppointments() {
  const navigate = useNavigate();
  const { data, isLoading } = useCustomerAppointments();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-muted animate-pulse rounded" />
        <div className="bg-white rounded-xl border border-border p-6 space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-muted animate-pulse rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-foreground">
            My Appointments
          </h2>
          <p className="text-muted-foreground mt-1">
            View and manage your appointments
          </p>
        </div>
        <button
          onClick={() => navigate({ to: "/" })}
          className="px-4 py-2 bg-gradient-primary text-white rounded-lg hover:opacity-90 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 whitespace-nowrap"
        >
          Book New Appointment
        </button>
      </div>

      {data?.length === 0 ? (
        <div className="bg-white rounded-xl border border-border p-12">
          <div className="text-center">
            <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No appointments found
            </h3>
            <p className="text-muted-foreground mb-6">
              You don't have any appointments scheduled yet.
            </p>
            <button
              onClick={() => navigate({ to: "/" })}
              className="px-6 py-3 bg-gradient-primary text-white rounded-lg hover:opacity-90 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Book Your First Appointment
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-border overflow-hidden">
          <div className="divide-y divide-border">
            {data?.map((appointment) => (
              <div
                key={appointment.id}
                className="p-6 hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() =>
                  navigate({
                    to: "/client-area/appointments/$id",
                    params: { id: appointment.id },
                  })
                }
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-foreground truncate">
                          {appointment.specialistName}
                        </h3>
                        {appointment.practiceName && (
                          <p className="text-sm text-muted-foreground mt-0.5 truncate">
                            {appointment.practiceName}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <StatusBadge
                          status={appointment.status}
                          type="primary"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 flex-shrink-0" />
                        <span>
                          {formatDisplayDate(appointment.startTime)}
                        </span>
                      </div>
                      <span className="text-muted-foreground/60">•</span>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 flex-shrink-0" />
                        <span>
                          {formatAppointmentTime(appointment.startTime)}
                        </span>
                      </div>
                      <span className="text-muted-foreground/60">•</span>
                      <span className="text-foreground font-medium">
                        {appointment.type === VisitType.Telemedicine
                          ? "Telemedicine"
                          : "In-Person"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
